import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';

import type { Product } from '@/types/products';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

export const useDetailsStore = defineStore('details', () => {
  const globalConfigurationStore = useGlobalConfigurationStore();
  const { globalConfiguration, apiURL } = storeToRefs(globalConfigurationStore);

  const distributorId = ref<string | null>(null);
  const fairguestWidgetId = ref<string | null>(null);
  const productDetails = ref<any>();
  const crossSellingActivities = ref<Product[]>([]);

  async function fetchDistributor() {
    const url = `${apiURL.value}/distributors/me`;
    const options = {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${globalConfiguration.value?.apikey}` },
    };
    try {
      const response = await fetch(url, options);
      const payload = await response.json();
      distributorId.value = payload?.user.distributorId ?? null;
      globalConfiguration.value!.distributorId = payload?.user.distributorId;
      fairguestWidgetId.value = payload.fairguestWidgetId ? payload.fairguestWidgetId.toString().trim() : null;
    }
    catch (err) {
      console.error('Error while loading user', err);
    }
  }

  async function fetchActivityDetails(id: string | number) {
    const url = `${apiURL.value}/products/${id}`;
    const options = {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${globalConfiguration.value?.apikey}` },
      body: JSON.stringify({
        properties: [{
          type: 'supplier',
        }, {
          type: 'rating',
        }, {
          type: 'nextSessionDate',
        }, {
          type: 'product_enrichment_by_distributor',
          distributorId: distributorId.value,
        }],
      }),
    };
    const { getDisplayedPrice } = useProductsStore();
    try {
      const response = await fetch(url, options);
      const activity = await response.json();
      activity.data.displayedPrice = getDisplayedPrice(activity.data);
      productDetails.value = activity.data ?? null;
    }
    catch (err) {
      console.error('Error while loading activity', err);
    }
  }

  async function fetchCrossSellingActivities(ids: string[]) {
    const { formatToProducts } = useProductsStore();
    const url = `${apiURL.value}/products/list`;
    const today = new Date();
    const nextMonth = new Date();
    nextMonth.setMonth(today.getMonth() + 1);
    const { currentLocale } = storeToRefs(globalConfigurationStore);
    const bodyV3 = {
      size: 8,
      sorts: [{ type: 'next_session' }],
      filters: [{
        filterProperties: {
          type: 'custom_filter',
          target: 'product',
          field: 'imported_in_langs',
        },
        selection: currentLocale.value,
      }, {
        filterProperties: {
          type: 'publicationStates',
          target: 'product',
        },
        selection: ['PUBLISHED'],
      }],
      properties: [{ type: 'supplier' }, { type: 'nextSessionDate' }, { type: 'rating' }],
    };
    if (ids.length > 0) {
      bodyV3.size = ids.length;
      bodyV3.filters.push({
        // @ts-expect-error unrecognized property
        filterProperties: {
          type: 'productIds',
        },
        selection: [...ids],
      });
    }
    else {
      bodyV3.filters.push({
        filterProperties: {
          type: 'custom_filter',
          target: 'product_enrichment',
          field: 'distributor_id',
        },
        // @ts-expect-error selection typing incorrect
        selection: distributorId.value,
      }, {
        filterProperties: {
          type: 'date_range',
        },
        selection: {
          startDate: today.getTime(),
          endDate: nextMonth.getTime(),
        },
      });
    }
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${globalConfiguration.value?.apikey}`,
      },
      body: JSON.stringify(bodyV3),
    };
    try {
      const response = await fetch(url, options);
      const products = await response.json();
      crossSellingActivities.value = [];
      if (products.data.length > 0) {
        crossSellingActivities.value = formatToProducts(products.data);
      }
    }
    catch (err) {
      console.error('Error while loading cross sellings', err);
    }
  }

  function getProviderWidgetUrl(locale: string) {
    const url = `${apiURL.value.slice(0, -3)}/static/widgets?apiKey=${globalConfiguration.value?.apikey}&productId=${productDetails.value?.id}&locale=${locale}`;
    let activityParams = '';
    switch (productDetails.value?.source) {
      case 'guidap':
        activityParams = `&g-path=/cart/add-to-cart/${productDetails.value?.sourceId}/undefined/DEPARTURE`;
        break;
      default:
        break;
    }
    return url + activityParams;
  }

  return {
    distributorId,
    fairguestWidgetId,
    productDetails,
    crossSellingActivities,
    fetchDistributor,
    fetchActivityDetails,
    fetchCrossSellingActivities,
    getProviderWidgetUrl,
  };
});
